/**
 * Calendar stage — structure-scoped, nested in the site dashboard.
 *
 * Rendered as a child route of SiteDashboardLayout, inside the shared dashboard
 * shell. Lets operators view, create, edit, and delete a site's scheduled relay
 * events on a MUI X Scheduler calendar (day/week/month/agenda) with relays as
 * resources. Recurrence is expanded server-side, so the calendar shows plain
 * per-occurrence events; edits and moves flow through the series-vs-occurrence
 * write path. Relay/unit data comes from the layout's odl_app snapshot (read via
 * context, plain JS — not re-fetched here); occurrences come from the calendar
 * occurrence hook.
 *
 * Contains:
 * - CalendarView — route child component (default export)
 */
import { Add, Tune } from '@mui/icons-material';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { parse, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import { DEFAULT_DATE_FORMAT } from '@sophocles/app/constants';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { loadCalendarSeries } from '@sophocles/features/calendar/api';
import {
  selectAllSeries,
  selectSeriesById,
  selectSelectedEventId,
  selectSelectedRelayId,
  setCalendarDate,
  setCalendarMode,
  setCalendarSelectedRelayId,
  setSelectedEventId,
} from '@sophocles/features/calendar/slice';
import { sitePath } from '@sophocles/router/paths';
import CalendarEventPopover from '@sophocles/views/sites/components/CalendarEventPopover';
import { sortRelays } from '@sophocles/views/sites/components/SiteDashboard/dashboardDerivations';
import SiteEventCalendar from '@sophocles/views/sites/components/SiteEventCalendar';
import CalendarEventDialog from '@sophocles/views/sites/dialogs/CalendarEventDialog';
import SeriesEditDialog from '@sophocles/views/sites/dialogs/SeriesEditDialog';
import useCalendarEventMutations, {
  INITIAL_SERIES_DIALOG_STATE,
} from '@sophocles/views/sites/hooks/useCalendarEventMutations';
import useCalendarOccurrences from '@sophocles/views/sites/hooks/useCalendarOccurrences';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';
import { buildResourceIdByRelayId, buildResources } from '@sophocles/views/sites/utils/schedulerEventAdapter';

const INITIAL_POPOVER_STATE = {
  open: false,
  anchorEl: null,
  event: null,
  occurrenceDate: null,
  mouseX: null,
  mouseY: null,
};

const EVENT_CHIP_SELECTOR = '[class*="odl-cal-evt-"]';

/** Split "<eventId>:<YYYY-MM-DD>" into its event id and occurrence date. */
function splitOccurrenceId(occurrenceId) {
  const separator = occurrenceId.indexOf(':');
  if (separator === -1) {
    return { eventId: occurrenceId, occurrenceDate: null };
  }
  return { eventId: occurrenceId.slice(0, separator), occurrenceDate: occurrenceId.slice(separator + 1) };
}

/**
 * Build the write payload for a dragged/resized occurrence: only the fields the
 * move changes (dates and clock-fixed on/off times, from the wall-time strings).
 * Both write paths are partial — a series patch leaves the rest of the series
 * untouched, an occurrence override applies just these to the single date — so
 * no recurrence or relay fields are sent.
 */
function buildDragPayload(intent) {
  return {
    id: intent.eventId,
    start_date: intent.newStart.split('T')[0],
    end_date: intent.newEnd.split('T')[0],
    on_timing: { type: 'time', timing_type: 'time', time: intent.newStart.split('T')[1], offset: 0 },
    off_timing: { type: 'time', timing_type: 'time', time: intent.newEnd.split('T')[1], offset: 0 },
  };
}

function CalendarView() {
  const params = useParams();
  const navigate = useNavigate();
  const { site, unit, loading } = useSiteDashboardLayoutContext();

  const siteId = params.site_id;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useUser();
  const weekStartsOn = user.get('options')?.get('start_of_week') === 'monday' ? 1 : 0;

  const selectedRelayId = useSelector(selectSelectedRelayId);
  const selectedEventId = useSelector(selectSelectedEventId);
  const series = useSelector(selectAllSeries);
  const seriesById = useSelector(selectSeriesById);
  const viewMode = useSelector(state => state.calendar.view.mode);
  const viewDateStr = useSelector(state => state.calendar.view.date);
  const visibleDate = useMemo(() => parse(viewDateStr, DEFAULT_DATE_FORMAT, new Date()), [viewDateStr]);

  const timeZone = site?.time_zone ?? null;
  const nextSunset = site?.next_sunset ?? null;
  const nextSunrise = site?.next_sunrise ?? null;
  const supportsNegativeOffsets = unit?.firmware_version?.supports_negative_schedule_offsets ?? false;

  const relays = useMemo(() => sortRelays(unit?.relays ?? []), [unit]);
  const resources = useMemo(() => buildResources(relays, t), [relays, t]);
  const resourceIdByRelayId = useMemo(() => buildResourceIdByRelayId(relays), [relays]);
  const relayNumbers = useMemo(() => relays.map(relay => relay.relay_number), [relays]);

  const query = new URLSearchParams(useLocation().search);
  const rawQueryRelayId = query.get('r');
  // Relay ids are numeric; the ?r= param arrives as a string and must be coerced
  // to match the numeric ids the relay selector and snapshot use.
  const queryRelayId =
    rawQueryRelayId != null && rawQueryRelayId !== '' && !Number.isNaN(Number(rawQueryRelayId))
      ? Number(rawQueryRelayId)
      : null;

  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editType, setEditType] = useState(null);
  const [eventPopover, setEventPopover] = useState(INITIAL_POPOVER_STATE);
  const [seriesEditDialog, setSeriesEditDialog] = useState(INITIAL_SERIES_DIALOG_STATE);

  const { events, loadData, loadDataDebounced } = useCalendarOccurrences({
    siteId,
    visibleDate,
    weekStartsOn,
    resourceIdByRelayId,
  });

  const { commandLocked, onSubmit, onDelete, handleSeriesEditConfirm } = useCalendarEventMutations({
    siteId,
    events: series,
    selectedDate,
    setSelectedDate,
    editType,
    setEditType,
    setIsEventDialogOpen,
    seriesEditDialog,
    setSeriesEditDialog,
    loadData,
    loadDataDebounced,
  });

  useEffect(() => {
    if (siteId != null) {
      dispatch(loadCalendarSeries({ siteId }));
    }
    if (queryRelayId != null) {
      dispatch(setCalendarSelectedRelayId(queryRelayId));
    }
  }, [siteId]);

  const handleViewChange = useCallback(view => dispatch(setCalendarMode(view)), [dispatch]);
  const handleVisibleDateChange = useCallback(date => dispatch(setCalendarDate(date)), [dispatch]);

  const handleEventActivate = useCallback(
    (occurrenceId, domEvent) => {
      const { eventId, occurrenceDate } = splitOccurrenceId(occurrenceId);
      const seriesRow = seriesById[eventId];
      if (!seriesRow) {
        return;
      }
      const anchorEl = domEvent.target?.closest?.(EVENT_CHIP_SELECTOR) ?? domEvent.target;
      setEventPopover({
        open: true,
        anchorEl,
        event: seriesRow,
        occurrenceDate,
        mouseX: domEvent.clientX ?? null,
        mouseY: domEvent.clientY ?? null,
      });
    },
    [seriesById],
  );

  const handleEventEdit = useCallback(
    (eventId, isRecurring, occurrenceDate) => {
      if (commandLocked) return;
      const seriesRow = seriesById[eventId];
      if (seriesRow) {
        dispatch(setCalendarSelectedRelayId(seriesRow.relay?.id ?? null));
        dispatch(setSelectedEventId(eventId));
        setSelectedDate(occurrenceDate);
        setIsEventDialogOpen(true);
        if (isRecurring) {
          setEditType('recurring');
        }
      }
      setEventPopover(INITIAL_POPOVER_STATE);
    },
    [commandLocked, dispatch, seriesById],
  );

  const handleEventDelete = useCallback(
    (eventId, isRecurring, occurrenceDate) => {
      if (commandLocked) return;
      setSeriesEditDialog({ open: true, isRecurring, actionType: 'delete', eventId, occurrenceDate });
      setEventPopover(INITIAL_POPOVER_STATE);
    },
    [commandLocked],
  );

  const handleEventDragResize = useCallback(
    intent => {
      if (commandLocked) return;
      const seriesRow = seriesById[intent.eventId];
      if (!seriesRow) {
        return;
      }
      const isRecurring = Boolean(seriesRow.recurrence?.parsed);
      const formValues = buildDragPayload(intent);
      if (isRecurring) {
        setSeriesEditDialog({
          open: true,
          isRecurring: true,
          actionType: 'dragResize',
          eventId: intent.eventId,
          formValues,
          occurrenceDate: intent.occurrenceDate,
        });
      } else {
        onSubmit(formValues);
      }
    },
    [commandLocked, seriesById, onSubmit],
  );

  // Relay editing lives on the site dashboard, not here — send operators there.
  const goToDashboard = () =>
    navigate(sitePath({ cust_id: params.cust_id, sitegroup_id: params.sitegroup_id, site_id: params.site_id }));

  // Toolbar "New event": keep the ?r= (or last) relay preselect when it drives a
  // load, else fall back to the first relay with loads so the dialog's selector
  // (which only offers relays with loads) opens on a valid one.
  const openNewEventDialog = () => {
    if (commandLocked) return;
    const preselectHasLoads = relays.some(r => r.id === selectedRelayId && (r.loads?.length ?? 0) > 0);
    if (!preselectHasLoads) {
      const firstWithLoads = relays.find(r => (r.loads?.length ?? 0) > 0);
      dispatch(setCalendarSelectedRelayId(firstWithLoads ? firstWithLoads.id : null));
    }
    dispatch(setSelectedEventId(null));
    setSelectedDate(null);
    setIsEventDialogOpen(true);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ pb: 2 }}>
      <Stack direction="column" spacing={1.5}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 'var(--radius-lg)' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('calendar.calendarEvents')}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" startIcon={<Tune />} onClick={goToDashboard}>
                {t('sites.editRelays', 'Edit relays')}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={openNewEventDialog}
                disabled={relays.length === 0 || commandLocked}
              >
                {t('calendar.actions.newEvent', 'New event')}
              </Button>
            </Stack>
          </Stack>
          <SiteEventCalendar
            events={events}
            resources={resources}
            relayNumbers={relayNumbers}
            displayTimezone={timeZone ?? undefined}
            view={viewMode}
            visibleDate={visibleDate}
            weekStartsOn={weekStartsOn}
            onViewChange={handleViewChange}
            onVisibleDateChange={handleVisibleDateChange}
            onEventActivate={handleEventActivate}
            onEventDragResize={handleEventDragResize}
            readOnly={commandLocked}
          />
        </Paper>
      </Stack>

      <CalendarEventDialog
        open={isEventDialogOpen}
        relays={relays}
        relayId={selectedRelayId}
        eventId={selectedEventId}
        selectedDate={selectedDate}
        siteTimeZone={timeZone}
        supportsNegativeOffsets={supportsNegativeOffsets}
        editType={editType}
        disabled={commandLocked}
        onSubmit={onSubmit}
        onDelete={onDelete}
        onClose={() => {
          setIsEventDialogOpen(false);
          setEditType(null);
          dispatch(setSelectedEventId(null));
          setSelectedDate(null);
        }}
      />

      <CalendarEventPopover
        open={eventPopover.open}
        anchorEl={eventPopover.anchorEl}
        event={eventPopover.event}
        occurrenceDate={eventPopover.occurrenceDate}
        mouseX={eventPopover.mouseX}
        mouseY={eventPopover.mouseY}
        disabled={commandLocked}
        onClose={() => setEventPopover(INITIAL_POPOVER_STATE)}
        onEdit={(eventId, isRecurring) => handleEventEdit(eventId, isRecurring, eventPopover.occurrenceDate)}
        onDelete={(eventId, isRecurring) => handleEventDelete(eventId, isRecurring, eventPopover.occurrenceDate)}
        nextSunrise={nextSunrise ? parseISO(nextSunrise) : null}
        nextSunset={nextSunset ? parseISO(nextSunset) : null}
      />

      <SeriesEditDialog
        open={seriesEditDialog.open}
        onClose={() => setSeriesEditDialog(INITIAL_SERIES_DIALOG_STATE)}
        onConfirm={handleSeriesEditConfirm}
        isRecurring={seriesEditDialog.isRecurring}
        actionType={seriesEditDialog.actionType}
        disabled={commandLocked}
      />
    </Box>
  );
}

export default CalendarView;
